import { FunctionComponent, useCallback, useMemo } from "react";
import { selectPlanView, selectStartingPoints } from "@store/plan/planReducer";
import { useAppSelector } from "@store/store";
import {
  selectNavigationDirectionIndex,
  selectDirections,
} from "@store/planRouteReducer";
import { ItemDirectionStepItems } from "@domain/ItemDirection";
import { PlanMarker } from "../elements/PlanMarker";
import { getCurrentDirections } from "./getCurrentDirections";

/**
 * Display markers for the user's plan.
 */
const PlanMarkers: FunctionComponent = () => {
  const navigationDirectionIndex = useAppSelector(
    selectNavigationDirectionIndex,
  );
  const planView = useAppSelector(selectPlanView);
  const directions = useAppSelector(selectDirections);
  const currentDirections = getCurrentDirections(
    directions,
    planView,
    navigationDirectionIndex,
  );
  const startingPoints = useAppSelector(selectStartingPoints);

  const handlePress = useCallback(
    (planItem: ItemDirectionStepItems) => () => {
      console.log(planItem);
    },
    [],
  );

  const markers = useMemo(() => {
    let ordinalCounter = 0;
    const result = currentDirections.reduce(
      (acc, direction, index) => {
        let ordinal = ordinalCounter;
        if (direction.start.items[0].type !== "StartPoint") {
          // Do not increment counter if start point, start points don't display ordinal.
          // If on navigate view, show the ordinal matching the slice of directions shown.
          ++ordinalCounter;
          ordinal =
            planView === "navigate"
              ? navigationDirectionIndex + ordinalCounter
              : ordinalCounter;
        }

        acc.push({ steps: direction.start, ordinal: ordinal });
        if (index === currentDirections.length - 1) {
          acc.push({ steps: direction.end, ordinal: ordinal + 1 });
        }

        return acc;
      },
      [] as { steps: ItemDirectionStepItems; ordinal: number }[],
    );

    return result;
  }, [currentDirections, navigationDirectionIndex, planView]);

  // Start points without plan items on the same day will not have directions, thus not present in
  // `markers`.
  const startingPointsWithNoPlanItemsOnDay = useMemo(() => {
    return startingPoints.filter((startingPoint) =>
      markers.every((marker) =>
        marker.steps.items.every(
          (item) => item.dayIndex !== startingPoint.dayIndex,
        ),
      ),
    );
  }, [markers, startingPoints]);

  return (
    <>
      {markers.map((item) => (
        <PlanMarker
          key={`${item.steps.locationKey}-${item.ordinal}`}
          coordinates={{
            latitude: item.steps.location.latitude,
            longitude: item.steps.location.longitude,
          }}
          type={item.steps.items[0].type}
          ordinal={item.ordinal}
          onPress={handlePress(item.steps)}
          zIndex={1000 - item.ordinal}
        />
      ))}
      {startingPointsWithNoPlanItemsOnDay.map((startingPoint) => (
        <PlanMarker
          key={`${startingPoint.location.latitude}-${startingPoint.location.longitude}-${startingPoint.dayIndex}`}
          coordinates={{
            latitude: startingPoint.location.latitude,
            longitude: startingPoint.location.longitude,
          }}
          type="StartPoint"
          zIndex={900}
          ordinal={-1}
        />
      ))}
    </>
  );
};

export { PlanMarkers };
